// Defines which fields exist in 'properties' of features

import { Feature, LineString, Point } from "geojson";
import { LatLngBounds } from "leaflet";

/* Common properties to all */

interface CommonProperties {
  site_id: string; // Generated by us, not by the backend!
  bounds: LatLngBounds;
  id: number; // Not unique! Only unique together with model_type
  content_pk: number;

  searchString: string;
}

/* Line features */

interface CommonLineProperties extends CommonProperties {
  connection_node_start_pk: number;
  connection_node_end_pk: number;
}

export interface ChannelProperties extends CommonLineProperties {
  model_type: "channels";
  code: string;
  kcu: number;
  dist_calc_points: number;
  discharge_coefficient: number;
  calculation_type: number; // Make enum
}

export interface Channel extends Feature<LineString> {
  properties: ChannelProperties;
}

export interface CulvertProperties extends CommonLineProperties {
  model_type: "culverts";
  display_name: string;
  code: string;
  kcu: number;
  cross_section_shape: number; // Make enum
  cross_section_width: number;
  cross_section_height: number; // Whether it is used depends on shape
  cross_section_table: number;
  friction_value: number;
  friction_type: number; // Make enum
  dist_calc_points: number;
  discharge_coefficient_positive: number;
  discharge_coefficient_negative: number;
  invert_level_start_point: number;
  invert_level_end_point: number;
  calculation_type: number; // Make enum
}

export interface Culvert extends Feature<LineString> {
  properties: CulvertProperties;
}

export interface PipeProperties extends CommonLineProperties {
  model_type: "pipes";
  display_name: string;
  material: number;
  kcu: number;
  friction_value: number;
  sewerage_type: number;
  cross_section_shape: number; // Make enum
  cross_section_width: number;
  cross_section_height: number; // Whether it is used depends on shape
  cross_section_table: number;
  invert_level_start_point: number;
  invert_level_end_point: number;
  discharge_coefficient: number;
  calculation_type: number; // Make enum
}

export interface Pipe extends Feature<LineString> {
  properties: PipeProperties;
}

export interface LeveeProperties extends CommonProperties {
  model_type: "levees";
  crest_level: number;
  max_breach_depth: number;
}

export interface Levee extends Feature<LineString> {
  properties: LeveeProperties;
}

/* Point features */

type CommonPointProperties = CommonProperties;

export interface BreachProperties extends CommonPointProperties {
  model_type: "breaches";
  code: string;
  display_name: string;
  levmat: number | null;
  levbr: number | null;
  levl: number;
  coordinates: [number, number];
}

export interface Breach extends Feature<LineString> {
  properties: BreachProperties;
}

export interface ManholeProperties extends CommonPointProperties {
  model_type: "manholes";
  display_name: string;
  calculation_type: number;
  sumax: number;
  bottom_level: number;
  surface_level: number;
  drain_level: number;
  width: number;
  shape: string; //XXX
  storage_area: string; //XXX
  initial_waterlevel: number;
}

export interface Manhole extends Feature<Point> {
  properties: ManholeProperties;
}

export interface NodeProperties extends CommonPointProperties {
  model_type: "nodes";
  dmax: number;
  sumax: number;
  node_type: number;
  coordinates: [number, number];
  zoom_category: number;
  seq_id: number;
  cell_coords: [number, number, number, number];
  bottom_level: number;
}

export interface Node extends Feature<Point> {
  properties: NodeProperties;
}

export interface OrificeProperties extends CommonLineProperties {
  model_type: "orifices";
  display_name: string;
  code: string;
  kcu: number;
  friction_value: number;
  friction_type: number; // Make enum
  discharge_coefficient_positive: number;
  discharge_coefficient_negative: number;
  crest_level: number;
  crest_type: number;
  gate_level: number;
  sewerage: number;
}

export interface Orifice extends Feature<LineString> {
  properties: OrificeProperties;
}

export interface PumpstationProperties extends CommonPointProperties {
  model_type: "pumps";
  display_name: string;
  start_level: number;
  lower_stop_level: number;
  bottom_level: number;
  capacity: number;
  type: string | null; // XXX
  line_geometry?: { coordinates: [number, number, number, number]; type: "LineString" };
}

export interface Pumpstation extends Feature<Point> {
  properties: PumpstationProperties;
}

export interface WeirProperties extends CommonPointProperties {
  model_type: "weirs";
  display_name: string;
  code: string;
  kcu: number;
  cross_section_shape: number;
  cross_section_width: number;
  cross_section_height: number;
  cross_section_table: number;
  crest_level: number;
  crest_type: number;
  sewerage: number;
  discharge_coefficient_positive: number;
  discharge_coefficient_negative: number;
  friction_value: number;
  friction_type: number;
  connection_node_start_pk: number;
  connection_node_end_pk: number;
}

export interface Weir extends Feature<Point> {
  properties: WeirProperties;
}

export type Asset =
  | Breach
  | Channel
  | Culvert
  | Levee
  | Manhole
  | Node
  | Orifice
  | Pipe
  | Pumpstation
  | Weir;

type KeysOfUnion<T> = T extends any ? keyof T : never;

// Two properties are used as special case.
export type PropertyName =
  | KeysOfUnion<Asset["properties"]>
  | "connection_node_end"
  | "connection_node_start";

// A type for an unknown asset's properties: an object that may have all properties, all optional
export type UnknownAssetProperties = { [property: string]: number };

export function displayName(asset: Asset): string {
  if ("display_name" in asset.properties) return asset.properties.display_name;

  const siteId = asset.properties.site_id;
  return (
    siteId.charAt(0).toUpperCase() + (siteId.length > 1 ? siteId.slice(1).replace("s_", " ") : "")
  );
}

export function isPotentialBreach(asset: Asset): asset is Breach {
  return asset.properties.model_type === "breaches";
}
